import { MdDialog, MdDialogConfig, MdDialogRef } from '@angular/material';
import {
    Component, NgModule, OnInit, Input, Output, EventEmitter, HostListener,
    ChangeDetectorRef, ViewChild, ViewContainerRef
} from '@angular/core';
import { EntityIdDetails } from 'app/shared/models/entity-id-details.model';
import { Agreement } from 'app/entities/transaction-profile/agreement.model';
import { EntityTransactionProfileService } from 'app/entities/transaction-profile/entity-transaction-profile.service';
import { Document } from 'app/shared/models/document.model';

import { ConstantsService } from 'app/services/constants-service';
import { SaveAgreementComponent } from 'app/entities/transaction-profile/agreements/saveagreement/save-agreement.component';
import { DocumentComponent } from 'app/entities/transaction-profile/documents/document.component';
import { AgreementDocumentsDialogComponent } from './agreement-documents-dialog.component';

import { TrackingService } from 'app/shared/services/tracking/google-analytics-events.service';
import { TrackingConstants } from 'app/shared/services/tracking/tracking-constants.service';

import { SnackBarService } from 'app/services/snackbar.service';
import { MessageService } from 'app/services/messages.service';

@Component({
    selector: 'agreement',
    templateUrl: './agreement.component.html'
})

export class AgreementComponent implements OnInit {
    @Input() entityIdDetails: EntityIdDetails;
    @ViewChild(DocumentComponent)
    private documentComponent: DocumentComponent;
    dialogRef: MdDialogRef<any>;
    agreements: Array<Agreement>;
    filteredAgreements: Array<Agreement>;
    currentlySelectedAgreement: Agreement;
    selectedDocument: Document;
    agreementsLoaded = false;
    dateFormat: string;
    searchKeyword: string = '';
    profileExportInProgress = false;

    constructor(
        private transactionProfileService: EntityTransactionProfileService,
        private dialog: MdDialog,
        private viewContainerRef: ViewContainerRef,
        private trackingService: TrackingService,
        private snackBarService: SnackBarService) { }

    ngOnInit() {
        this.dateFormat = ConstantsService.DATE_FORMAT;
        this.loadAgreements(0);
    }

    loadAgreements(selectedAgreementId) {
        if (this.entityIdDetails) {
            this.transactionProfileService.getAgreements(this.entityIdDetails.entityId)
                .subscribe(response => {
                    if (response) {
                        this.agreements = response;
                        this.filteredAgreements = response;
                        this.agreementsLoaded = true;
                        if (this.agreements.length > 0) {
                            // Highlight the selected list item in agreement list.
                            this.selectAgreementRow(this.agreements[selectedAgreementId]);
                        }else {
                            // if there is no agreement.
                            this.transactionProfileService.setSelectedAgreement(null);
                        }
                    }
                });
        }
    }

    openSaveAgreementPopup(selectedAgreement: Agreement) {
        const config = new MdDialogConfig();
        config.disableClose = true;
        config.viewContainerRef = this.viewContainerRef;

        this.dialogRef = this.dialog.open(SaveAgreementComponent, config);
        this.dialogRef.componentInstance.entityId = this.entityIdDetails.entityId;
        this.dialogRef.componentInstance.agreementDetails = selectedAgreement;
        if (selectedAgreement) {
            this.dialogRef.componentInstance.action = 'Edit';
        } else {
            this.dialogRef.componentInstance.action = 'Add';
        }
        this.dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.loadAgreements(0);
            }
            this.dialogRef = null;
        });
    }

    // Metod to return the array index of the selected agreement record.
    getSelectedAgreementIndex(selectedAgreementId) {
        const selectedAgreementIndex = 0;
        const index = this.agreements.findIndex(agreement => agreement.id === selectedAgreementId);
        return index !== -1 ? index : selectedAgreementIndex;
    }

    // Method to get executed on particular agreement row selection.
    selectAgreementRow(agreementEvent, optionSelectedAgreementId?: number) {
        let agreementId = null;
        let selectedAgreement: Agreement = null;
        if (agreementEvent && agreementEvent.id) {
            agreementId = agreementEvent.id;
            selectedAgreement = agreementEvent;
        } else if (agreementEvent && agreementEvent.data && agreementEvent.data.id) {
            agreementId = agreementEvent.data.id;
            selectedAgreement = agreementEvent.data;
        } else if (optionSelectedAgreementId) {
            agreementId = optionSelectedAgreementId;
        } else if (agreementEvent && typeof agreementEvent === 'object' && agreementEvent.id) {
            // Handle case where agreementEvent is the agreement object itself
            selectedAgreement = agreementEvent;
            agreementId = agreementEvent.id;
        }
        if (agreementId && !selectedAgreement) {
            // Find the agreement in the full agreements array
            selectedAgreement = this.agreements.find(a => a.id === agreementId);
        }
        if (selectedAgreement) {
            // Highlight the selected list item in agreement list.
            this.currentlySelectedAgreement = selectedAgreement;
            this.transactionProfileService.setSelectedAgreement(this.currentlySelectedAgreement);
        }
    }

    // Method to get executed on particular agreement row unselection.
    unselectAgreementRow(agreementEvent) {
        // Nothing as of now. It may be updated later.
    }

    removeAgreement(agreement: Agreement): void {
        const transProfileAgreementModel = new Agreement();
        transProfileAgreementModel.id = agreement.id;
        this.transactionProfileService.removeAgreement(transProfileAgreementModel)
            .subscribe(response => {
                if (response.flagResult) {
                    this.agreements.splice(this.agreements.indexOf(agreement), 1);
                }
                this.trackingService
                    .emitEvent(
                    TrackingConstants.TRANS_PROFILE_AGREEMENT,
                    TrackingConstants.DELETE_AGREEMENT,
                    TrackingConstants.SUCCESS, 1);
                this.callSnackBarServiceForAgreementRemoval(response);
            }, err => {
                this.trackingService
                    .emitEvent(
                    TrackingConstants.TRANS_PROFILE_AGREEMENT,
                    TrackingConstants.DELETE_AGREEMENT,
                    TrackingConstants.FAILURE, 0);
                this.callSnackBarServiceForAgreementRemoval(err);
            }
            );
    }

    callSnackBarServiceForAgreementRemoval(response: any) {
        this.snackBarService
            .showStatus(response,
            MessageService.AGREEMENT_DELETE_SUCCESS,
            MessageService.AGREEMENT_DELETE_FAILURE);
    }

    filterAgreements() {
        if (!this.searchKeyword || this.searchKeyword.trim() === '') {
            this.filteredAgreements = this.agreements;
        } else {
            const keyword = this.searchKeyword.toLowerCase().trim();
            this.filteredAgreements = this.agreements.filter(agreement => {
                const description = agreement.description ? agreement.description.toLowerCase() : '';
                return description.includes(keyword);
            });
        }
    }

    onSearchChange() {
        this.filterAgreements();
    }

    openDocumentsDialog(agreement: Agreement) {
        const config = new MdDialogConfig();
        config.disableClose = false;
        config.width = '80%';
        config.viewContainerRef = this.viewContainerRef;
        config.data = {
            agreement: agreement,
            entityIdDetails: this.entityIdDetails
        };

        this.dialogRef = this.dialog.open(AgreementDocumentsDialogComponent, config);
        
        this.dialogRef.afterClosed().subscribe(result => {
            if (result) {
                // Reload agreements to refresh document indicators
                this.loadAgreements(0);
            }
            this.dialogRef = null;
        });
    }

    downloadProfileExport() {
        if (!this.entityIdDetails || !this.entityIdDetails.entityId) {
            this.snackBarService.showSimpleMsj('Cannot generate export: Entity ID not available');
            return;
        }

        this.profileExportInProgress = true;
        this.transactionProfileService.downloadClientProfileExport(this.entityIdDetails.entityId).subscribe(
            (result) => {
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(result.blob);
                link.download = result.fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);
                this.profileExportInProgress = false;
            },
            error => {
                this.profileExportInProgress = false;
                this.snackBarService.showSimpleMsj('Error generating export: ' + (error.message || 'Unknown error'));
            }
        );
    }

    downloadReport() {
        if (!this.entityIdDetails || !this.entityIdDetails.entityId) {
            this.snackBarService.showSimpleMsj('Cannot download report: Entity ID not available');
            return;
        }

        const url = `${ConstantsService.AGREEMENTS}/${this.entityIdDetails.entityId}/report/word`;
        this.transactionProfileService.downloadReport(url).subscribe(
            (blob: Blob) => {
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = `Transactional_Relationships_Report_${this.entityIdDetails.entityId}.docx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);
            },
            error => {
                this.snackBarService.showSimpleMsj('Error downloading report: ' + (error.message || 'Unknown error'));
            }
        );
    }

}



// WEBPACK FOOTER //
// ./src/app/entities/transaction-profile/agreements/agreement.component.ts